import { useState, useEffect, useCallback } from "react";
import { analyticsApi, type Analytics, type Forecast } from "../api/analytics";
import "./Analytics.css";

export default function Analytics() {
  const [data, setData] = useState<Analytics | null>(null);
  const [forecast, setForecast] = useState<Forecast | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const [aRes, fRes] = await Promise.all([analyticsApi.get(), analyticsApi.forecast()]);
    if (aRes.data) setData(aRes.data);
    if (fRes.data) setForecast(fRes.data);
    if (aRes.error) setError(aRes.error);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div>
        <div className="screen-header"><h1>Analytics</h1><p>Insights, charts, and forecasts</p></div>
        <p className="an-status">Cargando datos…</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <div className="screen-header"><h1>Analytics</h1><p>Insights, charts, and forecasts</p></div>
        <p className="an-empty">Sin datos aún. Registra ventas para ver análisis.</p>
      </div>
    );
  }

  const maxFlavor = data.flavorVelocity[0]?.count ?? 1;

  return (
    <div>
      <div className="screen-header">
        <h1>Analytics</h1>
        <p>Insights, charts, and forecasts</p>
      </div>

      {error && <div className="an-error" onClick={() => setError("")}>❌ {error} ✕</div>}

      {/* ─── KPI Cards ─────────────────────────── */}
      <div className="an-kpis">
        <div className="an-kpi">
          <span className="an-kpi__label">Ganancia total</span>
          <span className="an-kpi__value an-kpi--green">${data.totalRevenue.toFixed(0)}</span>
        </div>
        <div className="an-kpi">
          <span className="an-kpi__label">Ventas</span>
          <span className="an-kpi__value">{data.totalSales}</span>
        </div>
        <div className="an-kpi">
          <span className="an-kpi__label">Stock actual</span>
          <span className="an-kpi__value">{data.batchStats.totalStock}</span>
        </div>
        <div className="an-kpi">
          <span className="an-kpi__label">Costo envíos</span>
          <span className="an-kpi__value">${data.shippingDelta.toFixed(0)}</span>
        </div>
      </div>

      {/* ─── Flavor Velocity ───────────────────── */}
      <section className="an-section">
        <h3 className="an-section__title">
          <span className="material-symbols-rounded">local_fire_department</span> Sabores más vendidos
        </h3>
        <div className="an-bar-chart">
          {data.flavorVelocity.slice(0, 8).map((f) => (
            <div key={`${f.model}|${f.flavor}`} className="an-bar-row">
              <span className="an-bar__label">{f.flavor}</span>
              <div className="an-bar__track">
                <div
                  className="an-bar__fill"
                  style={{ width: `${(f.count / maxFlavor) * 100}%` }}
                />
              </div>
              <span className="an-bar__count">{f.count}</span>
            </div>
          ))}
          {data.flavorVelocity.length === 0 && (
            <p className="an-empty-sm">Sin datos de sabores aún</p>
          )}
        </div>
      </section>

      {/* ─── Monthly Revenue ───────────────────── */}
      <section className="an-section">
        <h3 className="an-section__title">
          <span className="material-symbols-rounded">trending_up</span> Ingresos mensuales
        </h3>
        <div className="an-bar-chart">
          {data.monthlyRevenue.map((m) => {
            const maxRev = Math.max(...data.monthlyRevenue.map((x) => x.revenue), 1);
            return (
              <div key={m.month} className="an-bar-row">
                <span className="an-bar__label">{m.month}</span>
                <div className="an-bar__track">
                  <div
                    className="an-bar__fill an-bar__fill--blue"
                    style={{ width: `${(m.revenue / maxRev) * 100}%` }}
                  />
                </div>
                <span className="an-bar__count">${m.revenue.toFixed(0)}</span>
              </div>
            );
          })}
          {data.monthlyRevenue.length === 0 && (
            <p className="an-empty-sm">Sin datos mensuales aún</p>
          )}
        </div>
      </section>

      {/* ─── Repeat Buyers ─────────────────────── */}
      <section className="an-section">
        <h3 className="an-section__title">
          <span className="material-symbols-rounded">repeat</span> Compradores frecuentes
        </h3>
        {data.repeatBuyers.length === 0 ? (
          <p className="an-empty-sm">Sin compradores recurrentes aún</p>
        ) : (
          <div className="an-table">
            <div className="an-table__header">
              <span>Teléfono</span><span>Compras</span><span>Total</span>
            </div>
            {data.repeatBuyers.map((b) => (
              <div key={b.phone} className="an-table__row">
                <span><span className="material-symbols-rounded">call</span> {b.phone.slice(-8)}</span>
                <span>{b.purchases}</span>
                <span>${b.totalSpent.toFixed(0)}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ─── Batch Stats ───────────────────────── */}
      <section className="an-section">
        <h3 className="an-section__title">
          <span className="material-symbols-rounded">pace</span> Ritmo de venta
        </h3>
        <div className="an-kpis">
          <div className="an-kpi">
            <span className="an-kpi__label">Vendidos</span>
            <span className="an-kpi__value">{data.batchStats.totalSold}</span>
          </div>
          <div className="an-kpi">
            <span className="an-kpi__label">Diario</span>
            <span className="an-kpi__value">{data.batchStats.dailyRate}/día</span>
          </div>
          <div className="an-kpi">
            <span className="an-kpi__label">Agotar en</span>
            <span className="an-kpi__value an-kpi--warn">
              {data.batchStats.daysToSellOut > 0 ? `${data.batchStats.daysToSellOut} días` : "—"}
            </span>
          </div>
        </div>
      </section>

      {/* ─── Forecast ──────────────────────────── */}
      {forecast && (
        <section className="an-section">
          <h3 className="an-section__title">
            <span className="material-symbols-rounded">psychology</span> Pronóstico y recomendación
          </h3>

          <div className="an-kpis">
            <div className="an-kpi">
              <span className="an-kpi__label">Presupuesto disponible</span>
              <span className="an-kpi__value an-kpi--green">${forecast.availableBudget.toFixed(0)}</span>
            </div>
            {forecast.daysToSellOut !== null && (
              <div className="an-kpi">
                <span className="an-kpi__label">Se agota en</span>
                <span className="an-kpi__value an-kpi--warn">{forecast.daysToSellOut} días</span>
              </div>
            )}
          </div>

          {forecast.recommendations.length > 0 && (
            <div style={{ marginTop: "0.75rem" }}>
              <h4 style={{ fontSize: "0.85rem", marginBottom: "0.5rem", color: "var(--color-text-secondary)" }}>
                Compra recomendada (con ${forecast.availableBudget.toFixed(0)}):
              </h4>
              {forecast.recommendations.map((r, i) => (
                <div key={i} className="an-recommendation">
                  <div className="an-rec__info">
                    <span className="an-rec__flavor">{r.flavor}</span>
                    <span className="an-rec__model">{r.model}</span>
                  </div>
                  <div className="an-rec__stats">
                    <span>{r.recommendedQty} uds.</span>
                    <span className="an-rec__cost">~${r.estimatedCost}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
